import {
  CompanyType,
  OnboardingPath,
  OnboardingLoadingState,
  ShopTypeEnum,
  OnboardingResponseState,
  StepName,
  BookDemoStepValue,
} from "@jurni/schemas";
import { create } from "zustand";
import { transformResponseToStoreState } from "@/utils/onboarding";

export interface OnboardingState {
  onboardingId: string | null;
  currentStepName: StepName | null;
  companyType: CompanyType | null;
  onboardingLoadingState: OnboardingLoadingState | null;
  onboardingStepsPaths: OnboardingPath[];
  stepsCount: number;
  bookDemoAnswer: BookDemoStepValue | null;
  shopConnected: {
    shopName: string | null;
    shopType: keyof typeof ShopTypeEnum | null;
  };

  // Actions
  setCompanyType: (type: CompanyType | null) => void;
  setBookDemoAnswer: (answer: BookDemoStepValue | null) => void;
  updateOnboardingState: (state: OnboardingResponseState) => void;
  resetOnboardingState: () => void;
}

const initialState = {
  onboardingId: null,
  currentStepName: null,
  companyType: null,
  onboardingLoadingState: null,
  onboardingStepsPaths: [],
  stepsCount: 3,
  bookDemoAnswer: null,
  shopConnected: { shopName: null, shopType: null },
} satisfies Partial<OnboardingState>;

const store = create<OnboardingState>()((set) => ({
  ...initialState,
  setCompanyType: (type) => set({ companyType: type }),
  setBookDemoAnswer: (answer) => set({ bookDemoAnswer: answer }),
  updateOnboardingState: (state: OnboardingResponseState) =>
    set(transformResponseToStoreState(state)),
  resetOnboardingState: () => set(initialState),
}));

export const onboardingProps: {
  [K in keyof OnboardingState]: (state: OnboardingState) => OnboardingState[K];
} = {
  onboardingId: (state: OnboardingState) => state.onboardingId,
  currentStepName: (state: OnboardingState) => state.currentStepName,
  companyType: (state: OnboardingState) => state.companyType,
  onboardingLoadingState: (state: OnboardingState) =>
    state.onboardingLoadingState,
  onboardingStepsPaths: (state: OnboardingState) => state.onboardingStepsPaths,
  stepsCount: (state: OnboardingState) => state.stepsCount,
  bookDemoAnswer: (state: OnboardingState) => state.bookDemoAnswer,
  shopConnected: (state: OnboardingState) => state.shopConnected,
  // Actions
  setCompanyType: (state: OnboardingState) => state.setCompanyType,
  setBookDemoAnswer: (state: OnboardingState) => state.setBookDemoAnswer,
  updateOnboardingState: (state: OnboardingState) =>
    state.updateOnboardingState,
  resetOnboardingState: (state: OnboardingState) => state.resetOnboardingState,
};

export const useOnboardingStore = store;
